/**
 * Report Page - Capture Timeline Analysis
 * Dedicated page for viewing captures and missed captures in a visual timeline grid
 * Green boxes = received, Red boxes = missed
 */

import { useEffect, useMemo, useCallback, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Typography, Breadcrumb, Skeleton, Empty } from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import AppLayout from "@/components/AppLayout";
import { TimelineGrid } from "@/components/TimelineGrid";
import { MissedStatusDialog } from "@/components/MissedStatusDialog";
import { PhotoViewer } from "@/components/PhotoViewer";
import { useEffectiveUserId } from "@/hooks/useEffectiveUserId";
import { useView } from "@/hooks/useView";
import { usePhotosInRange } from "@/hooks/usePhotosInRange";
import { useMissedCapturesForReport } from "@/hooks/useMissedCapturesForReport";
import type { Photo, MissedCapture } from "@/types";
import styles from "./ReportPage.module.css";

const { Title, Text } = Typography;

export default function ReportPage() {
  const { viewId } = useParams<{ viewId: string }>();
  const { effectiveUid } = useEffectiveUserId();
  const { view, loading: viewLoading } = useView(viewId || "");

  // Modal states
  const [selectedMissed, setSelectedMissed] = useState<MissedCapture | null>(null);
  const [missedDialogOpen, setMissedDialogOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);

  // Fixed date range: last 7 days
  const { start, end } = useMemo(() => {
    const now = new Date();
    return {
      start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
      end: now,
    };
  }, []);

  // Get captureOrigin for slot alignment
  const captureOrigin = useMemo(() => {
    const originMs = view?.androidStatus?.capturing?.captureOrigin?.toMillis?.();
    return originMs ? new Date(originMs) : undefined;
  }, [view?.androidStatus?.capturing?.captureOrigin]);

  // Fetch photos in range
  const { photos, loading: photosLoading } = usePhotosInRange({
    userId: effectiveUid || "",
    viewId: viewId || "",
    rangeStart: start,
    rangeEnd: end,
    enabled: !!effectiveUid && !!viewId,
  });

  // Sort photos reverse chronologically (newest first) for PhotoViewer navigation
  const sortedPhotos = useMemo(() => {
    return [...photos].sort((a, b) => {
      const aTime = a.capturedAt?.toDate?.()?.getTime() ?? 0;
      const bTime = b.capturedAt?.toDate?.()?.getTime() ?? 0;
      return bTime - aTime;
    });
  }, [photos]);

  // Calculate missed captures and get timeline events
  const { missedCaptures, timelineEvents, loading: missedLoading } =
    useMissedCapturesForReport({
      userId: effectiveUid || "",
      projectId: view?.projectId || "",
      viewId: viewId || "",
      photos,
      timerSettings: view?.timerSettings,
      rangeStart: start,
      rangeEnd: end,
      captureOrigin,
      enabled: !!effectiveUid && !!viewId && !!view,
    });

  // Safety valve: never hold the skeleton longer than 4s waiting for the
  // photos/missed computations — the view doc itself stays a hard gate
  const [forceReveal, setForceReveal] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setForceReveal(true), 4000);
    return () => window.clearTimeout(t);
  }, []);
  // Single reveal: view + photos + missed captures behind ONE skeleton.
  // If the valve fires, TimelineGrid still receives `loading` and shows its
  // own placeholder for the remaining data.
  const pageReady =
    !viewLoading && (forceReveal || (!photosLoading && !missedLoading));

  // Handle photo click from timeline
  const handlePhotoClick = useCallback((_photo: Photo, index: number) => {
    setSelectedPhotoIndex(index);
    setPhotoViewerOpen(true);
  }, []);

  // Handle missed capture click from timeline
  const handleMissedClick = useCallback((missed: MissedCapture) => {
    setSelectedMissed(missed);
    setMissedDialogOpen(true);
  }, []);

  // Handle photo viewer navigation
  const handlePhotoNavigate = useCallback((index: number) => {
    setSelectedPhotoIndex(index);
  }, []);

  // Handle photo viewer close
  const handlePhotoViewerClose = useCallback(() => {
    setPhotoViewerOpen(false);
    setSelectedPhotoIndex(null);
  }, []);

  // Handle missed dialog close
  const handleMissedDialogClose = useCallback(() => {
    setMissedDialogOpen(false);
    setSelectedMissed(null);
  }, []);

  const isLoading = viewLoading || photosLoading || missedLoading;

  if (!pageReady) {
    // Single stable skeleton shaped like the real page (breadcrumb + header +
    // timeline area) — view, photos and missed-captures reveal in one shot
    return (
      <AppLayout>
        <Skeleton
          active
          title={false}
          paragraph={{ rows: 1, width: 240 }}
          className={styles.breadcrumb}
          style={{ marginBottom: 16 }}
        />
        <div className={styles.header}>
          <Title level={3}>
            <MaterialIcon name="analytics" size={28} style={{ marginRight: 8 }} />
            Capture Timeline
          </Title>
          <Skeleton active title={false} paragraph={{ rows: 1, width: 180 }} style={{ margin: 0 }} />
        </div>
        <Skeleton active title={false} paragraph={{ rows: 10 }} />
      </AppLayout>
    );
  }

  if (!view) {
    return (
      <AppLayout>
        <Empty description="View not found" />
      </AppLayout>
    );
  }

  // Get current photo for viewer
  const currentPhoto =
    selectedPhotoIndex !== null && sortedPhotos[selectedPhotoIndex]
      ? sortedPhotos[selectedPhotoIndex]
      : null;

  return (
    <AppLayout>
      {/* Breadcrumb */}
      <Breadcrumb
        className={styles.breadcrumb}
        items={[
          {
            title: <Link to="/">Projects</Link>,
          },
          {
            title: <Link to={`/photos/${viewId}`}>{view.name}</Link>,
          },
          {
            title: "Report",
          },
        ]}
      />

      {/* Header */}
      <div className={styles.header}>
        <Title level={3}>
          <MaterialIcon name="analytics" size={28} style={{ marginRight: 8 }} />
          Capture Timeline
        </Title>
        <Text type="secondary">{view.name} • Last 7 days</Text>
      </div>

      {/* Timeline Grid */}
      <TimelineGrid
        photos={photos}
        missedCaptures={missedCaptures}
        timelineEvents={timelineEvents}
        loading={isLoading}
        onPhotoClick={handlePhotoClick}
        onMissedClick={handleMissedClick}
        timezone={view?.androidStatus?.device?.timezone}
      />

      {/* Photo Viewer Modal */}
      {photoViewerOpen && currentPhoto && (
        <PhotoViewer
          photo={currentPhoto}
          photos={sortedPhotos}
          currentIndex={selectedPhotoIndex ?? 0}
          onClose={handlePhotoViewerClose}
          onNavigate={handlePhotoNavigate}
          timezone={view?.androidStatus?.device?.timezone}
        />
      )}

      {/* Missed Status Dialog */}
      <MissedStatusDialog
        missedCapture={selectedMissed}
        open={missedDialogOpen}
        onClose={handleMissedDialogClose}
        timezone={view?.androidStatus?.device?.timezone}
      />
    </AppLayout>
  );
}
